import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useCreateFakeSpeiPaymentMutation } from '@/api/v1';
import Copy from '@/assets/icons/copy.svg?react';
import { LabeledAmount } from '@/components/molecules/LabeledAmount';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { SHOULD_MOCK_DEPOSITS } from '@/constants';
import { useCustomer } from '@/hooks/useCustomer';
import { useProcessedTxAmounts } from '@/hooks/useProcessedTxAmounts';
import { useToast } from '@/hooks/useToast';
import { useTransactionNotification } from '@/hooks/useTransactionNotification';
import { useTranslator } from '@/hooks/useTranslator';
import { getCustomerTaxId } from '@/types/customer';
import { LabelAndValue } from '@/types/labelAndValue';
import {
  IntegratorOperation,
  StatusType,
  TransactionNotification,
  TransactionResponse,
} from '@/types/transaction';
import { isSpeiInLocalInfo } from '@/types/transaction.ext';

interface Props {
  readonly transaction: TransactionResponse;
  readonly successPath: string;
}

interface Spei {
  readonly clabe: string | null;
  readonly bankName: string | null;
  readonly beneficiaryName: string | null;
}

export const SpeiDepositModalContent: React.FC<Props> = (props: Props): React.ReactElement => {
  const { transaction, successPath } = props;

  const toast = useToast();
  const tr = useTranslator();
  const customer = useCustomer();

  const MAX_BENEFICIARY_NAME_LENGTH = 20;

  const [createFakeSpeiTransaction] = useCreateFakeSpeiPaymentMutation();
  const [isCopying, setIsCopying] = React.useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();
  const notifications = useTransactionNotification(transaction?.id ?? '');

  const { fromAmount, fromCurrency, toAmount, toCurrency } = useProcessedTxAmounts(transaction);

  const spei = React.useMemo((): Spei => {
    const spei = isSpeiInLocalInfo(transaction.localInfo)
      ? transaction.localInfo
      : {
          speiInClabe: '',
          speiInBankName: '',
          speiInBeneficiaryName: '',
        };

    const { speiInClabe, speiInBankName, speiInBeneficiaryName } = spei;

    return {
      clabe: speiInClabe ?? null,
      bankName: speiInBankName,
      beneficiaryName: speiInBeneficiaryName.slice(0, MAX_BENEFICIARY_NAME_LENGTH),
    };
  }, [transaction, MAX_BENEFICIARY_NAME_LENGTH]);

  const copyToClipboard = React.useCallback(async (): Promise<void> => {
    if (SHOULD_MOCK_DEPOSITS) {
      const taxId = getCustomerTaxId(customer);

      createFakeSpeiTransaction({
        type: 'SPEI',
        transactionId: transaction.id,
        documentId: taxId,
      });
    } else {
      const { clipboard } = navigator;
      const { clabe } = spei;

      if (clabe !== null) {
        setIsCopying(true);
        try {
          await clipboard.writeText(clabe);
          toast.show(tr('SPEI_CLABE_COPIED'), 'success');
        } catch {
          toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        } finally {
          setIsCopying(false);
        }
      }
    }
  }, [createFakeSpeiTransaction, customer, spei, toast, tr, transaction.id]);

  React.useEffect((): void => {
    const deposit = notifications.find(
      (notification: TransactionNotification): boolean =>
        notification.operation === IntegratorOperation.SPEI_DEPOSIT_RECEIVED,
    );

    if (deposit?.status === StatusType.SUCCEEDED) {
      const search = new URLSearchParams(location.search);

      search.set('transactionId', transaction.id);
      navigate({
        pathname: successPath,
        search: search.toString(),
      });
    }
  }, [location.search, navigate, notifications, successPath, transaction.id]);

  const entries = React.useMemo(
    (): readonly LabelAndValue[] => [
      {
        label: 'CLABE',
        value: spei.clabe ?? '',
      },
      {
        label: tr('SPEI_BANK_BENEFICIARY'),
        value: spei.beneficiaryName ?? '',
      },
      {
        label: tr('SPEI_BANK_NAME'),
        value: spei.bankName ?? '',
      },
    ],
    [spei.bankName, spei.beneficiaryName, spei.clabe, tr],
  );

  return (
    <Paper elevation={1}>
      <Box display="flex" flexDirection="column" gap={3} py={3} px={4}>
        <Box sx={styles.codeContainer}>
          <Box sx={styles.codeStringAndButtonContainer}>
            <Box sx={styles.codeStringBox}>
              {entries.map((entry: LabelAndValue): React.ReactNode => {
                return (
                  <Typography key={entry.label} fontSize={14} fontWeight={400} lineHeight="24px">
                    <Typography
                      component="span"
                      fontSize="inherit"
                      fontWeight={600}
                      lineHeight="inherit"
                      whiteSpace="nowrap">
                      {entry.label}:{' '}
                    </Typography>

                    <Typography
                      sx={styles.codeString}
                      component="span"
                      fontSize="inherit"
                      fontWeight="inherit"
                      lineHeight="inherit">
                      {entry.value}
                    </Typography>
                  </Typography>
                );
              })}
            </Box>
            <LoadingButton
              variant="dark"
              size="compact"
              startIcon={<Copy fill="currentColor" />}
              disabled={spei.clabe === null}
              loading={isCopying}
              onClick={copyToClipboard}>
              {tr('COPY_CLABE')}
            </LoadingButton>
          </Box>
        </Box>
        <Box>
          <LabeledAmount label={tr('YOU_SEND')} amount={fromAmount} currency={fromCurrency} />
          <LabeledAmount label={tr('YOU_RECEIVE')} amount={toAmount} currency={toCurrency} />
        </Box>
        <Box textAlign="center">
          <Typography fontSize={16} fontWeight={400}>
            <Typography fontSize="inherit" fontWeight="inherit" component="span">
              {tr('QUOTE_WILL_EXPIRE')}{' '}
            </Typography>
            <Typography
              fontSize="inherit"
              fontWeight="inherit"
              component="span"
              color="primary.main">
              {tr('TEN_MIN')}
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const styles: Record<string, SystemStyleObject> = {
  codeContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'custom.Primary1Background',
    padding: 4,
    gap: 3,
    borderRadius: 3,
  },
  seeAllLink: {
    cursor: 'pointer',
  },
  codeString: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  codeStringBox: {
    flex: 1,
    mb: 0,
  },
  codeStringAndButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    minWidth: 0,
    flex: 1,
  },
};
